/**
 * Componente principal del modo académico
 * Muestra checklist expandible y gestiona actividades
 */

import React, { useState, useEffect } from 'react';
import { academicService, UserLessonProgress, LessonObjective, LevelProgress } from '../../services/academicService';
import { personalizedTaskService } from '../../services/personalizedTaskService';
import webSocketService from '../../services/webSocketService';
import LessonChecklist from './LessonChecklist';
import EvaluationResults from './EvaluationResults';
import LevelProgressMap from './LevelProgressMap';
import { useScreenOrientation } from '../../hooks/useScreenOrientation';
import './AcademicMode.css';

// Constantes para las claves de sessionStorage
const STORAGE_KEY_SHOW_MAP = 'academic_mode_show_map';
const STORAGE_KEY_LAST_LESSON = 'academic_mode_last_lesson';

interface AcademicModeProps {
  userEmail: string;
  showProgressMap?: boolean;
  onClose?: () => void;
  onActivityStart?: () => void;
  onActivityEnd?: () => void;
}

const AcademicMode: React.FC<AcademicModeProps> = ({ userEmail, showProgressMap = false, onClose, onActivityStart, onActivityEnd }) => {
  const [currentLevel, setCurrentLevel] = useState<string>('A1.1');
  const [currentUnit, setCurrentUnit] = useState<number>(0);
  const [currentLesson, setCurrentLesson] = useState<number>(1);
  const [objectives, setObjectives] = useState<LessonObjective[]>([]);
  const [progress, setProgress] = useState<UserLessonProgress | null>(null);
  const [levelProgress, setLevelProgress] = useState<LevelProgress[]>([]);
  const [isActivityActive, setIsActivityActive] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [resultsUnit, setResultsUnit] = useState<number | null>(null);
  const [resultsLesson, setResultsLesson] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(() => {
    // Verificar primero si hay una lección activa
    const academicMode = webSocketService.getAcademicMode();
    if (academicMode.isActive) {
      return false; // Si hay lección activa, mostrar objetivos
    }
    
    // Si no hay lección activa, restaurar estado guardado o usar true por defecto (primera vez)
    const savedShowMap = sessionStorage.getItem(STORAGE_KEY_SHOW_MAP);
    return savedShowMap !== null ? savedShowMap === 'true' : true;
  });
  const orientation = useScreenOrientation();

  // Flag para evitar recargar datos cuando solo se está minimizando/maximizando el panel
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  
  // Flag para rastrear si ya se mostró la evaluación automáticamente para esta lección
  const [evaluationAutoShown, setEvaluationAutoShown] = useState<string>('');
  
  // Al cargar: restaurar lección vista (nivel/unidad/lección) pero NO marcar "En progreso".
  // "En progreso" solo se muestra cuando el usuario hace clic en "Iniciar Lección" en esta sesión.
  const checkIfLessonIsActive = () => {
    const academicMode = webSocketService.getAcademicMode();
    if (academicMode.isActive && academicMode.level && 
        academicMode.unit !== undefined && academicMode.lesson !== undefined) {
      
      // Restaurar la lección que tenía abierta (para mostrar sus objetivos)
      if (academicMode.level !== currentLevel || 
          academicMode.unit !== currentUnit || 
          academicMode.lesson !== currentLesson) {
        setCurrentLevel(academicMode.level);
        setCurrentUnit(academicMode.unit);
        setCurrentLesson(academicMode.lesson);
      }
      setShowMap(false); // Mostrar objetivos, no el mapa
      // No establecer setIsActivityActive(true): en sesión nueva el usuario debe ver Iniciar/Reanudar/Completado según progreso
      return true;
    }
    return false;
  };

  useEffect(() => {
    const academicMode = webSocketService.getAcademicMode();
    let shouldLoadActiveLesson = false;
    
    if (academicMode.isActive && academicMode.level && 
        academicMode.unit !== undefined && academicMode.lesson !== undefined) {
      // Restaurar lección que tenía abierta (no marcar "En progreso" en sesión nueva)
      if (academicMode.level !== currentLevel || 
          academicMode.unit !== currentUnit || 
          academicMode.lesson !== currentLesson) {
        setCurrentLevel(academicMode.level);
        setCurrentUnit(academicMode.unit);
        setCurrentLesson(academicMode.lesson);
        setShowMap(false);
        shouldLoadActiveLesson = true;
      }
      // No llamar a setIsActivityActive(true): en sesión nueva se muestra Iniciar/Reanudar/Completado según progreso
    }
    
    if (!hasLoadedOnce || shouldLoadActiveLesson) {
      loadInitialData().then(() => {
        setHasLoadedOnce(true);
        checkIfLessonIsActive();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);
  
  // Actualizar progreso cuando cambia showMap (abrir/cerrar mapa) sin recargar todo
  useEffect(() => {
    if (showMap && !isActivityActive && hasLoadedOnce) {
      // Solo actualizar progreso del nivel, no recargar todo
      academicService.getLevelProgress(userEmail, currentLevel).then(setLevelProgress).catch(console.error);
    }
  }, [showMap]);
  
  // Cargar progreso del nivel cuando se muestra el mapa por primera vez
  useEffect(() => {
    if (showMap && userEmail && currentLevel && !isActivityActive) {
      academicService.getLevelProgress(userEmail, currentLevel).then(setLevelProgress).catch(console.error);
    }
  }, [showMap, userEmail, currentLevel]);

  // Guardar estado cuando cambia showMap
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY_SHOW_MAP, showMap.toString());
  }, [showMap]);

  // Guardar última lección vista
  useEffect(() => {
    if (currentLevel && currentUnit !== undefined && currentLesson !== undefined) {
      sessionStorage.setItem(STORAGE_KEY_LAST_LESSON, 
        JSON.stringify({ level: currentLevel, unit: currentUnit, lesson: currentLesson }));
    }
  }, [currentLevel, currentUnit, currentLesson]);

  const handleLessonSelect = async (unit: number, lesson: number) => {
    // Verificar si hay una lección activa diferente
    const academicMode = webSocketService.getAcademicMode();
    if (academicMode.isActive && 
        (academicMode.level !== currentLevel || 
         academicMode.unit !== unit || 
         academicMode.lesson !== lesson)) {
      // Si se selecciona una lección diferente a la activa, resetear el estado
      setIsActivityActive(false);
      if (onActivityEnd) {
        onActivityEnd();
      }
    }
    
    setCurrentUnit(unit);
    setCurrentLesson(lesson);
    // Ocultar el mapa para mostrar la información de la lección
    setShowMap(false);
    // Resetear flag de evaluación mostrada para nueva lección
    setEvaluationAutoShown('');
    // Recargar datos para la nueva lección (forzar recarga)
    await loadInitialData(true);
    setHasLoadedOnce(false); // Resetear flag para nueva lección
    
    // Verificar si la nueva lección está activa
    checkIfLessonIsActive();
  };

  const loadInitialData = async (forceReload: boolean = false) => {
    // Si hay una actividad activa y no es una recarga forzada, solo actualizar progreso
    if (isActivityActive && !forceReload && hasLoadedOnce) {
      try {
        // Solo actualizar el progreso sin recargar objetivos
        const progressData = await academicService.getUserLessonProgress(userEmail, currentLevel, currentUnit, currentLesson);
        if (progressData) {
          setProgress(progressData);
          // Verificar si se completó mientras estaba cerrado
          if (progressData.all_objectives_completed && progressData.rubric_evaluation) {
            setShowResults(true);
          }
        }
        // Actualizar progreso del nivel para el mapa
        const levelProgressData = await academicService.getLevelProgress(userEmail, currentLevel);
        setLevelProgress(levelProgressData);
      } catch (error) {
        console.error('[AcademicMode] Error al actualizar progreso:', error);
      }
      return;
    }
    
    setLoading(true);
    try {
      // PRIMERO: Verificar si hay una lección activa
      const academicMode = webSocketService.getAcademicMode();
      if (academicMode.isActive && academicMode.level && 
          academicMode.unit !== undefined && academicMode.lesson !== undefined) {
        // Usar la lección activa
        setCurrentLevel(academicMode.level);
        setCurrentUnit(academicMode.unit);
        setCurrentLesson(academicMode.lesson);
        setShowMap(false); // Mostrar objetivos
      } else {
        // SEGUNDO: Intentar restaurar última lección vista
        const savedLastLesson = sessionStorage.getItem(STORAGE_KEY_LAST_LESSON);
        if (savedLastLesson) {
          try {
            const lastLesson = JSON.parse(savedLastLesson);
            setCurrentLevel(lastLesson.level);
            setCurrentUnit(lastLesson.unit);
            setCurrentLesson(lastLesson.lesson);
            // Restaurar estado de showMap (ya se hace en el useState)
          } catch (e) {
            console.warn('[AcademicMode] Error al restaurar última lección:', e);
          }
        }
        
        // TERCERO: Si no hay lección guardada, obtener desde Apolearn
        if (!savedLastLesson) {
          const academicLevel = await academicService.getUserAcademicLevel();
          if (academicLevel) {
            setCurrentLevel(academicLevel.level);
            if (academicLevel.unit) setCurrentUnit(academicLevel.unit);
            if (academicLevel.lesson) setCurrentLesson(academicLevel.lesson);
          }
        }
      }

      // Cargar objetivos y progreso (que incluye la tarea personalizada)
      const [objectivesData, progressData] = await Promise.all([
        academicService.getLessonObjectives(currentLevel, currentUnit, currentLesson),
        academicService.getUserLessonProgress(userEmail, currentLevel, currentUnit, currentLesson),
      ]);

      setObjectives(objectivesData);
      setProgress(progressData);

      // Si no hay tarea personalizada en el progreso, generarla
      if (progressData && !progressData.personalized_task) {
        const task = await academicService.getPersonalizedTask(userEmail, currentLevel, currentUnit, currentLesson);
        if (task && progressData) {
          // Actualizar el progreso con la tarea generada
          const updatedProgress = await academicService.updateUserLessonProgress(
            userEmail,
            currentLevel,
            currentUnit,
            currentLesson,
            { personalized_task: task }
          );
          if (updatedProgress) {
            setProgress(updatedProgress);
          }
        }
      }

      // Cargar progreso del nivel completo (siempre, para el mapa)
      const levelProgressData = await academicService.getLevelProgress(userEmail, currentLevel);
      setLevelProgress(levelProgressData);

      // Si es la primera carga, encontrar la lección más nueva desbloqueada
      if (!hasLoadedOnce && levelProgressData.length > 0) {
        const latestLesson = academicService.findLatestUnlockedLesson(levelProgressData);
        if (latestLesson) {
          setCurrentUnit(latestLesson.unit);
          setCurrentLesson(latestLesson.lesson);
          // Recargar datos para la lección encontrada
          const [objectivesData, progressDataLatest] = await Promise.all([
            academicService.getLessonObjectives(currentLevel, latestLesson.unit, latestLesson.lesson),
            academicService.getUserLessonProgress(userEmail, currentLevel, latestLesson.unit, latestLesson.lesson),
          ]);
          setObjectives(objectivesData);
          setProgress(progressDataLatest);
        }
      }

      // Si todos los objetivos están completados, mostrar resultados
      if (progressData?.all_objectives_completed && progressData.rubric_evaluation) {
        setShowResults(true);
        setResultsUnit(currentUnit);
        setResultsLesson(currentLesson);
      }
    } catch (error) {
      console.error('[AcademicMode] Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Callback cuando se completa la evaluación de la rúbrica
  // Nota: handleEvaluationComplete se maneja internamente en EvaluationResults
  // Comentado porque no se usa actualmente (se removió de setAcademicMode)
  /*
  const handleEvaluationComplete = async () => {
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const progressData = await academicService.getUserLessonProgress(
        userEmail,
        currentLevel,
        currentUnit,
        currentLesson
      );
        hasProgress: !!progressData,
        hasEvaluation: !!progressData?.rubric_evaluation,
        allCompleted: progressData?.all_objectives_completed
      });
      
      if (progressData) {
        setProgress(progressData);
        
        if (progressData.rubric_evaluation) {
          const lessonKey = `${currentLevel}-${currentUnit}-${currentLesson}`;
          const alreadyShown = evaluationAutoShown === lessonKey;
          
          if (!alreadyShown) {
            setShowResults(true);
            setResultsUnit(currentUnit);
            setResultsLesson(currentLesson);
            setEvaluationAutoShown(lessonKey);
            setShowMap(false);
            if (onActivityEnd) {
              onActivityEnd();
            }
          } else {
          }
        } else {
          console.warn('[AcademicMode] ⚠️ No hay evaluación en el progreso recargado');
        }
        
        const levelProgressData = await academicService.getLevelProgress(userEmail, currentLevel);
        setLevelProgress(levelProgressData);
      } else {
        console.warn('[AcademicMode] ⚠️ No se pudo obtener el progreso');
      }
    } catch (error) {
      console.error('[AcademicMode] Error al actualizar después de evaluación:', error);
    }
  };
  */

  const handleRegenerateTask = async () => {
    try {
      // Forzar regeneración de la tarea (incrementará task_version)
      const newTask = await personalizedTaskService.regenerateTask(
        userEmail,
        currentLevel,
        currentUnit,
        currentLesson
      );
      
      if (newTask) {
        // Recargar el progreso para obtener la nueva tarea y versión actualizada
        const updatedProgress = await academicService.getUserLessonProgress(
          userEmail,
          currentLevel,
          currentUnit,
          currentLesson
        );
        if (updatedProgress) {
          setProgress(updatedProgress);
        }
      }
    } catch (error) {
      console.error('[AcademicMode] Error al regenerar tarea:', error);
      alert('Error al generar nueva tarea. Por favor intenta de nuevo.');
    }
  };

  const handleStartActivity = async () => {
    setIsActivityActive(true);
    
      // Activar modo académico en WebSocket (solo guarda el estado)
      if (objectives.length > 0) {
        webSocketService.setAcademicMode(
          currentLevel,
          currentUnit,
          currentLesson,
          objectives,
          userEmail,
          handleObjectiveUpdate,
          progress?.personalized_task
        );
      
      // Iniciar actividad: cambiar prompt reference y reconectar WebSocket
      try {
        await webSocketService.startLessonActivity();
        // Notificar que la actividad ha comenzado
        if (onActivityStart) {
          onActivityStart();
        }
      } catch (error) {
        console.error('[AcademicMode] Error al iniciar actividad:', error);
        setIsActivityActive(false);
        if (onActivityEnd) {
          onActivityEnd();
        }
      }
    }
  };

  const handleObjectiveUpdate = async (objectiveDescription: string, isCorrect: boolean) => {
    const updatedProgress = await academicService.updateObjectiveStatus(
      userEmail,
      currentLevel,
      currentUnit,
      currentLesson,
      objectiveDescription,
      isCorrect
    );

    if (updatedProgress) {
      setProgress(updatedProgress);

      // Si todos los objetivos están completados
      if (updatedProgress.all_objectives_completed) {
        const lessonKey = `${currentLevel}-${currentUnit}-${currentLesson}`;
        const alreadyShown = evaluationAutoShown === lessonKey;
        
        // Si ya hay evaluación, mostrarla inmediatamente (solo si no se mostró antes)
        if (updatedProgress.rubric_evaluation && !alreadyShown) {
          setShowResults(true);
          setResultsUnit(currentUnit);
          setResultsLesson(currentLesson);
          setEvaluationAutoShown(lessonKey); // Marcar como mostrada
          // Notificar que la actividad ha terminado (lección completada)
          if (onActivityEnd) {
            onActivityEnd();
          }
        } else if (!updatedProgress.rubric_evaluation && !alreadyShown) {
          // Si no hay evaluación aún, esperar a que se complete la evaluación en el WebSocket
          // La evaluación se ejecuta automáticamente en el WebSocket cuando se completan todos los objetivos
          // Esperar un poco y recargar el progreso para verificar si ya se guardó la evaluación
          
          // Intentar recargar el progreso varias veces hasta que aparezca la evaluación
          let attempts = 0;
          const maxAttempts = 10; // Máximo 5 segundos (10 intentos * 500ms)
          const checkEvaluation = async () => {
            attempts++;
            try {
              const progressData = await academicService.getUserLessonProgress(
                userEmail,
                currentLevel,
                currentUnit,
                currentLesson
              );
              
              if (progressData?.rubric_evaluation && !alreadyShown) {
                // Evaluación encontrada, mostrarla
                setProgress(progressData);
                setShowResults(true);
                setResultsUnit(currentUnit);
                setResultsLesson(currentLesson);
                setEvaluationAutoShown(lessonKey); // Marcar como mostrada
                // Notificar que la actividad ha terminado (lección completada)
                if (onActivityEnd) {
                  onActivityEnd();
                }
              } else if (attempts < maxAttempts) {
                // Aún no hay evaluación, esperar un poco más
                setTimeout(checkEvaluation, 500);
              } else {
                // Timeout: no se encontró evaluación después de varios intentos
                console.warn('[AcademicMode] ⚠️ No se encontró evaluación después de varios intentos');
              }
            } catch (error) {
              console.error('[AcademicMode] Error al verificar evaluación:', error);
            }
          };
          
          // Iniciar verificación después de un pequeño delay
          setTimeout(checkEvaluation, 500);
        }
      }
    }
  };

  if (loading) {
    return (
      <div className={`academic-mode ${orientation}`}>
        <div className="academic-mode-content">
          <div className="academic-mode-loading">
            <div className="loading-spinner"></div>
            <p>Cargando información académica...</p>
          </div>
        </div>
      </div>
    );
  }

  const handleRetryLesson = async () => {
    try {
      // Resetear progreso de la lección
      const resetProgress = await academicService.resetLessonProgress(
        userEmail,
        currentLevel,
        currentUnit,
        currentLesson
      );
      
      if (resetProgress) {
        setProgress(resetProgress);
        setShowResults(false);
        setIsActivityActive(false);
        // Resetear flag de evaluación mostrada para permitir mostrar de nuevo
        setEvaluationAutoShown('');
        // Desactivar modo académico en WebSocket
        webSocketService.clearAcademicMode();
        // Notificar que la actividad ha terminado
        if (onActivityEnd) {
          onActivityEnd();
        }
        // Recargar datos (forzar recarga después de resetear)
        await loadInitialData(true);
        setHasLoadedOnce(false); // Resetear flag después de reintentar
      }
    } catch (error) {
      console.error('[AcademicMode] Error al reintentar lección:', error);
    }
  };

  const handleShowResults = async (unit: number, lesson: number) => {
    try {
      const progressData = await academicService.getUserLessonProgress(userEmail, currentLevel, unit, lesson);
      
      if (progressData?.rubric_evaluation) {
        // Actualizar unidad y lección actuales para mostrar los resultados
        setCurrentUnit(unit);
        setCurrentLesson(lesson);
        setProgress(progressData);
        setResultsUnit(unit);
        setResultsLesson(lesson);
        setShowResults(true);
        // Ocultar el mapa para mostrar los resultados
        setShowMap(false);
      } else {
        console.warn('[AcademicMode] ⚠️ No hay evaluación disponible para esta lección');
      }
    } catch (error) {
      console.error('[AcademicMode] ❌ Error al obtener resultados:', error);
    }
  };

  return (
    <div className={`academic-mode ${orientation}`}>
      <div className="academic-mode-content">
        {orientation === 'portrait' && (
          <div className="academic-mode-header-portrait">
            <div className="academic-mode-header-portrait-top">
              <button
                className={`academic-mode-map-button ${showMap ? 'active' : ''}`}
                onClick={() => setShowMap(!showMap)}
                aria-label={showMap ? "Ocultar mapa" : "Mostrar mapa"}
                title={showMap ? "Ocultar mapa" : "Mostrar mapa"}
              >
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  className="academic-mode-map-icon"
                >
                  <path 
                    d="M3 3V21L9 18L15 21L21 18V3L15 6L9 3L3 6Z" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <h2>Ruta académica</h2>
              {onClose && (
                <button
                  className="academic-mode-close-button"
                  onClick={onClose}
                  aria-label="Cerrar"
                >
                  <img 
                    src="/assets/ui/botones/Boton_Cerrar.png" 
                    alt="Cerrar"
                    className="academic-mode-close-icon"
                  />
                </button>
              )}
            </div>
            {!showMap && (
              <div className="academic-mode-level">
                {currentLevel} - Unidad {currentUnit} - Lección {currentLesson}
              </div>
            )}
          </div>
        )}

        {orientation === 'landscape' && (
          <div className="academic-mode-header">
            <div className="academic-mode-header-top">
              <button
                className={`academic-mode-map-button ${showMap ? 'active' : ''}`}
                onClick={() => setShowMap(!showMap)}
                aria-label={showMap ? "Ocultar mapa" : "Mostrar mapa"}
                title={showMap ? "Ocultar mapa" : "Mostrar mapa"}
              >
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  className="academic-mode-map-icon"
                >
                  <path 
                    d="M3 3V21L9 18L15 21L21 18V3L15 6L9 3L3 6Z" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <h2>Ruta académica</h2>
              {onClose && (
                <button
                  className="academic-mode-close-button-landscape"
                  onClick={onClose}
                  aria-label="Cerrar"
                  title="Cerrar"
                >
                  <img 
                    src="/assets/ui/botones/Boton_Cerrar.png" 
                    alt="Cerrar"
                    className="academic-mode-close-icon"
                  />
                </button>
              )}
            </div>
            {!showMap && (
              <div className="academic-mode-level">
                {currentLevel} - Unidad {currentUnit} - Lección {currentLesson}
              </div>
            )}
          </div>
        )}

        {showMap && (
          <>
            <div className="academic-mode-progress-map-wrapper">
              <LevelProgressMap
                level={currentLevel}
                progress={levelProgress}
                currentUnit={currentUnit}
                currentLesson={currentLesson}
                onLessonSelect={handleLessonSelect}
                onShowResults={handleShowResults}
              />
            </div>
            <div className="academic-mode-bottom-spacer"></div>
          </>
        )}

        {!showMap && (
          <>
            {showResults && progress?.rubric_evaluation && resultsUnit !== null && resultsLesson !== null ? (
              <div className="academic-mode-results-container">
                <EvaluationResults
                  evaluation={progress.rubric_evaluation}
                  onClose={async () => {
                    setShowResults(false);
                    setResultsUnit(null);
                    setResultsLesson(null);
                    // Actualizar progreso después de cerrar resultados
                    await loadInitialData(true);
                    const levelProgressData = await academicService.getLevelProgress(userEmail, currentLevel);
                    setLevelProgress(levelProgressData);
                  }}
                  onContinue={async () => {
                    setShowResults(false);
                    setResultsUnit(null);
                    setResultsLesson(null);
                    // Actualizar progreso después de continuar
                    await loadInitialData(true);
                    const levelProgressData = await academicService.getLevelProgress(userEmail, currentLevel);
                    setLevelProgress(levelProgressData);
                  }}
                  onRetry={progress.rubric_evaluation.overall_score < 70 ? handleRetryLesson : undefined}
                />
              </div>
            ) : (
              <>
                {/* Tarea personalizada */}
                {progress?.personalized_task && (
                  <div className="academic-mode-task-container">
                    <div className="academic-mode-task-header">
                      <h3>Tâche</h3>
                      <button
                        className="academic-mode-task-refresh-button"
                        onClick={handleRegenerateTask}
                        title="Generar una nueva tarea personalizada"
                        aria-label="Regenerar tarea"
                      >
                        🔄
                      </button>
                    </div>
                    <div className="academic-mode-task-description">
                      {progress.personalized_task}
                    </div>
                  </div>
                )}

                <LessonChecklist
                  objectives={objectives}
                  progress={progress}
                  onObjectiveUpdate={handleObjectiveUpdate}
                  isActivityActive={isActivityActive}
                />

                {/* Mostrar botón de iniciar solo si es la lección actual (en progreso) */}
                <div className="academic-mode-start-button-wrapper">
                  {(() => {
                    // Si está activa, mostrar botón deshabilitado con "En progreso"
                    if (isActivityActive) {
                      return (
                        <button
                          className="academic-mode-start-button"
                          disabled={true}
                        >
                          En progreso
                        </button>
                      );
                    }
                    
                    // Si no está activa, mostrar botón normal
                    // Verificar si esta lección está en progreso
                    const lessonProgress = levelProgress.find(
                      p => p.unit === currentUnit && p.lesson === currentLesson
                    );
                    const isInProgress = lessonProgress?.inProgress || false;
                    
                    // También verificar si hay progreso en la base de datos (objetivos con intentos)
                    const hasProgress = progress && Object.values(progress.objectives_status).some(
                      (status: any) => status.correct_count > 0 || status.total_correct > 0
                    );
                    
                    // Mostrar botón si está en progreso o si hay progreso en la base de datos
                    // O si es la lección más reciente desbloqueada (lección actual)
                    const latestLesson = academicService.findLatestUnlockedLesson(levelProgress);
                    const isCurrentLesson = latestLesson && 
                      latestLesson.unit === currentUnit && 
                      latestLesson.lesson === currentLesson;
                    
                    const shouldShowStartButton = isInProgress || hasProgress || isCurrentLesson;
                    
                    if (!shouldShowStartButton) {
                      return null;
                    }
                    
                    // Texto del botón según progreso: Iniciar / Reanudar / Completado (nunca "En progreso" si no ha iniciado en esta sesión)
                    let buttonText = 'Iniciar Lección';
                    if (progress?.all_objectives_completed) {
                      buttonText = 'Completado';
                    } else if (hasProgress) {
                      buttonText = 'Reanudar';
                    }
                    
                    return (
                      <button
                        className="academic-mode-start-button"
                        onClick={handleStartActivity}
                        disabled={progress?.all_objectives_completed}
                      >
                        {buttonText}
                      </button>
                    );
                  })()}
                </div>
              </>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default AcademicMode;

